import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Trash2, Lightbulb, Bug, Sparkles, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const STATUSES = ["new", "reviewing", "planned", "done", "declined"];
const CAT_ICON = { improvement: Sparkles, idea: Lightbulb, bug: Bug, other: MessageSquare };
const STATUS_COLOR = {
  new: "bg-[#144F75]/10 text-[#144F75]", reviewing: "bg-[#C9A24B]/15 text-[#8a6d1f]",
  planned: "bg-[#D98E73]/15 text-[#a5583f]", done: "bg-emerald-100 text-emerald-700",
  declined: "bg-[#E5E0D8] text-[#4A5D6B]",
};

export default function SuggestionsPanel() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");
  const { data: items = [] } = useQuery({ queryKey: ["admin-suggestions", filter], queryFn: () => api.get(`/admin/suggestions${filter ? `?status=${filter}` : ""}`).then((r) => r.data) });

  const setStatus = useMutation({
    mutationFn: ({ id, status }) => api.put(`/admin/suggestions/${id}`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-suggestions"] }); toast.success("Updated — your changes are live"); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/suggestions/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-suggestions"] }); toast.success("Deleted — your changes are live"); },
  });

  return (
    <div data-testid="admin-suggestions-panel">
      <h2 className="font-serif text-2xl text-[#1A242B] mb-1">Suggestions & feedback</h2>
      <p className="text-sm text-[#4A5D6B] mb-5">Improvement ideas, feature requests, and issues from visitors & members.</p>
      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={() => setFilter("")} className={`rounded-full px-3 py-1 text-sm border ${!filter ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>All</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`rounded-full px-3 py-1 text-sm border capitalize ${filter === s ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>{s}</button>
        ))}
      </div>
      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-[#8FA89B]">No suggestions yet.</p>}
        {items.map((s) => {
          const Icon = CAT_ICON[s.category] || MessageSquare;
          return (
            <div key={s.id} data-testid={`suggestion-${s.id}`} className="bg-white rounded-xl border border-[#E5E0D8] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <Icon size={18} className="text-[#144F75] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm text-[#1A242B]">{s.message}</p>
                    <p className="text-xs text-[#8FA89B] mt-1">
                      {s.name || "Anonymous"}{s.email ? ` · ${s.email}` : ""} · {s.category} · {new Date(s.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <button onClick={() => del.mutate(s.id)} className="text-[#B45151] hover:text-[#8f3f3f] shrink-0"><Trash2 size={15} /></button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {STATUSES.map((st) => (
                  <button key={st} onClick={() => setStatus.mutate({ id: s.id, status: st })}
                    className={`rounded-full px-2.5 py-1 text-xs capitalize ${s.status === st ? STATUS_COLOR[st] + " font-semibold ring-1 ring-current" : "bg-[#F9F7F3] text-[#8FA89B] hover:text-[#4A5D6B]"}`}>
                    {st}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
